import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Framer Motion
import { AnimatePresence, motion } from 'framer-motion';

// I18n
import { I18n, Translate } from 'react-redux-i18n';

// Spectrum Components
import Autocomplete from '@react/react-spectrum/Autocomplete';
import SearchField from '@react/react-spectrum/Search';
import Dialog from '@react/react-spectrum/Dialog';
import { ActionButton } from '@adobe/react-spectrum';
import CrossSmall from '@spectrum-icons/ui/CrossSmall';

// Search Actions
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import fetchSuggestions from '../../actions/searchActions';

// React Router
import { withRouter } from '../withRouter';

// Utils
import { addCommaSeparator } from '../../util/util';

// CSS
import '../../styles/Search.scss';

const fetchSuggestionsDebounced = AwesomeDebouncePromise(fetchSuggestions, 800, { leading: true });

const Search = ({ totalResults, router, programPath }) => {
  const [searchTerm, setSearchTermState] = useState('');
  const [fullScreenSearchVisible, setFullScreenSearchVisible] = useState(false);
  const [noResultsFlag, setNoResultsFlag] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const isMobile = useRef(false);
  const observer = useRef(null);

  const handleMutations = (mutationsList) => {
    if (fullScreenSearchVisible) {
      mutationsList.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.classList && node.classList.contains('spectrum-Popover') && !node.classList.contains('fs-autocomplete-popover')) {
              setTimeout(() => {
                node.classList.add('fs-autocomplete-popover');
              }, 10);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    setSearchTermState('');
    setFullScreenSearchVisible(false);
  }, [programPath]);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 600px)');
    isMobile.current = mql.matches;

    // Create the MutationObserver
    observer.current = new MutationObserver(handleMutations);
    observer.current.observe(document.body, { childList: true, subtree: true });

    // Event listener to detect clicks outside the search box
    const handleClickOutside = (event) => {
      const noResultEle = document.getElementById('resultEle');
      if (noResultEle && !noResultEle.contains(event.target)) {
        setNoResultsFlag(false);
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      observer.current.disconnect();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchAutoCompleteData = async (text) => {
    const result = await fetchSuggestionsDebounced(text, programPath);
    setNoResultsFlag(!(result.length > 0));
    setSearchResults(result);
    return result;
  };

  const setSearchTerm = async (term) => {
    setSearchTermState(term);
    setNoResultsFlag(!term && false);
    await fetchAutoCompleteData(term);
  };

  const handleSelectItem = (item) => {
    const programType = programPath === 'spp' ? 'solution' : 'technology';
    router.navigate(`/${programType}/${item.url}`);
  };

  const renderAutocompleteItem = (item) => {
    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    if (searchTerm && searchTerm.match(/^ *$/) === null && item.name.match(new RegExp(escapeRegex(searchTerm), 'i')) !== null) {
      const matchBolded = item.name.replace(new RegExp(`(${escapeRegex(searchTerm)})`, 'gi'), '<strong>$1</strong>');
      const htmlObject = { __html: matchBolded };

      return (
        <div
          key={item.name}
          className={!fullScreenSearchVisible && 'autocomplete-itemRow'}
          onClick={() => handleSelectItem(item)}
          onKeyDown={() => handleSelectItem(item)}
          role="button"
          tabIndex="0"
        >
          <span className="autocomplete-company-name" dangerouslySetInnerHTML={htmlObject} />
        </div>
      );
    }

    return (
      <div
        key={item.name}
        className={!fullScreenSearchVisible && 'autocomplete-itemRow'}
        onClick={() => handleSelectItem(item)}
        onKeyDown={() => handleSelectItem(item)}
        role="button"
        tabIndex="0"
      >
        <span className="autocomplete-company-name">{item.name}</span>
      </div>
    );
  };

  const renderFullScreenSearchDialog = () => {
    if (fullScreenSearchVisible) {
      return (
        <Dialog
          isDismissible
          mode="fullscreenTakeover"
          open
          role="dialog"
          trapFocus
          className="full-screen-search-overlay"
        >
          <Autocomplete
            renderItem={renderAutocompleteItem}
            getCompletions={fetchAutoCompleteData}
            value={searchTerm}
            onSelect={handleSelectItem}
          >
            <SearchField
              autoFocus
              placeholder={I18n.t('partner_finder.search.search_number_by_name', { results: addCommaSeparator(totalResults) })}
              onChange={setSearchTerm}
              className="full-screen-dialog-searchbox"
            />
            <ActionButton
              aria-label="Icon only"
              UNSAFE_className="clear-mobile-search"
              onPress={() => setFullScreenSearchVisible(false)}
            >
              <CrossSmall />
            </ActionButton>
          </Autocomplete>
          {searchTerm && searchTerm.length > 1 && noResultsFlag && (
            <div className="mobile-noresults">
              <Translate value="partner_finder.search.no_results" />
              <br />
              <Translate value="partner_finder.search.please_try_another_word" />
            </div>
          )}
        </Dialog>
      );
    }
    return null;
  };

  const renderSearchBox = () => {
    if (isMobile.current) {
      return (
        <>
          <SearchField
            data-test-id="mobile-search-field"
            placeholder={I18n.t('partner_finder.search.search_number_by_name', { results: addCommaSeparator(totalResults) })}
            readOnly
            onClick={() => setFullScreenSearchVisible(true)}
            width="300px"
          />
          {renderFullScreenSearchDialog()}
        </>
      );
    }

    return (
      <>
        <SearchField
          placeholder={I18n.t('partner_finder.search.search_for_partners')}
          onChange={setSearchTerm}
          value={searchTerm}
        />
        <AnimatePresence>
          <motion.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ ease: 'easeInOut', duration: 0.2 }}
          >
            {searchResults.length > 0 && (
              <div className="result-wrapper" id="resultEle">
                {searchResults.map((item) => renderAutocompleteItem(item))}
              </div>
            )}
            {searchTerm && searchTerm.length > 1 && noResultsFlag && (
              <div className="result-wrapper noResults" id="resultEle">
                <Translate value="partner_finder.search.no_results" />
                <br />
                <Translate value="partner_finder.search.please_try_another_word" />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </>
    );
  };

  return (
    <div className="cmp-search">
      {renderSearchBox()}
    </div>
  );
};

Search.propTypes = {
  totalResults: PropTypes.number.isRequired,
  router: PropTypes.shape({
    navigate: PropTypes.func,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  totalResults: state.results.totalResults,
  programPath: state.facets.programPath,
});

export default withRouter(connect(mapStateToProps)(Search));
